<template>
    <tr class="row-details">
        <td colspan="4">
            <div id="info-panel" class="panel panel-default">
                <div class="panel-overlay" v-if="isLoading"></div>

                <div class="panel-heading">
                    <div class="panel-title">Informations</div>
                </div>

                <div class="panel-body">
                    <div v-if="!isUpdatingUser">
                        <dl class="flex-list">
                            <div class="flex-item">
                                <dt>ID</dt>
                                <dd>{{ user.id }}</dd>
                            </div>

                            <div class="flex-item">
                                <dt>Type</dt>
                                <dd>{{ user.decoded_type }}</dd>
                            </div>

                            <div class="flex-item">
                                <dt>Nom</dt>
                                <dd @mouseover="showEditNameIcon = true" @mouseout="showEditNameIcon = false" class="editable" @click="onNameEditIconClick">
                                    {{ userFullName }} <i class="fa fa-pencil m-l-10" v-show="showEditNameIcon"></i>
                                </dd>
                            </div>
                        </dl>

                        <dl class="flex-list">
                            <div class="flex-item">
                                <dt>Téléphone</dt>
                                <dd @mouseover="showEditPhoneIcon = true" @mouseout="showEditPhoneIcon = false" class="editable" @click="onPhoneEditIconClick">
                                    {{ userPhoneNumber }} <i class="fa fa-pencil m-l-10" v-show="showEditPhoneIcon"></i>
                                </dd>
                            </div>

                            <div class="flex-item">
                                <dt>Email</dt>
                                <dd @mouseover="showEditEmailIcon = true" @mouseout="showEditEmailIcon = false" class="editable" @click="onEmailEditIconClick">
                                    {{ user.email }} <i class="fa fa-pencil m-l-10" v-show="showEditEmailIcon"></i>
                                </dd>
                            </div>

                            <div class="flex-item">
                                <dt>Mot de passe</dt>
                                <dd @mouseover="showEditPasswordIcon = true" @mouseout="showEditPasswordIcon = false" class="editable" @click="onPasswordEditIconClick">
                                    ******** <i class="fa fa-pencil m-l-10" v-show="showEditPasswordIcon"></i>
                                </dd>
                            </div>
                        </dl>
                    </div>

                    <user-name-update-form v-else-if="isUpdatingUserName" 
                        :user="user"
                        @operation-canceled="isUpdatingUserName = false"
                        @name-updated="onUserNameUpdate">
                    </user-name-update-form>
                    
                    <user-phone-update-form v-else-if="isUpdatingUserPhone" 
                        :user="user"
                        @operation-canceled="isUpdatingUserPhone = false"
                        @phone-updated="onUserPhoneUpdate">
                    </user-phone-update-form>
                    
                    <user-email-update-form v-else-if="isUpdatingUserEmail" 
                        :user="user"
                        @operation-canceled="isUpdatingUserEmail = false"
                        @email-updated="onUserEmailUpdate">
                    </user-email-update-form>

                    <user-password-update-form v-else-if="isUpdatingUserPassword" 
                        :user="user"
                        @operation-canceled="isUpdatingUserPassword = false"
                        @password-updated="onUserPasswordUpdate">
                    </user-password-update-form>
                </div>
            </div>

            <div id="customers-panel" class="panel panel-default" v-if="!isUpdatingUser">
                <div class="panel-overlay" v-if="isLoading"></div>

                <div class="panel-heading">
                    <div class="panel-title">Client(s) associé(s)</div>
                </div>

                <div class="panel-body">
                    <div class="confirmation-overlay" v-if="customerToDetach">
                        <div class="overlay-inner">
                            <p>Êtes-vous certain de vouloir dissocier le client <strong>{{ customerToDetachName }}</strong> de cet utilisateur ?</p>

                            <div class="buttons-container">
                                <div type="button" class="btn btn-primary btn-sm" @click="onCustomerDetachmentConfirm">Valider</div>
                                <div type="button" class="btn btn-default btn-sm" @click="onCustomerDetachmentCancel">Annuler</div>
                            </div>
                        </div>
                    </div>

                    <table class="table" v-if="customers.length > 0">
                        <thead>
                            <tr>
                                <th>Nom</th>
                                <th class="text-center">SAP</th>
                                <th>Représentant légal principal</th>
                                <th class="text-center"></th>
                            </tr>
                        </thead>

                        <tbody>
                            <tr v-for="customer in customers" :key="customer.id">
                                <td>
                                    <router-link :to="{ name: 'customer', params: { id: customer.id } }">
                                        {{ customer.name }}
                                    </router-link>
                                </td>

                                <td class="text-center">
                                    <template v-if="customer.stored_in_erp">#{{ customer.erp_id }}</template>
                                    <template v-else>-</template>
                                </td>

                                <td>
                                    {{ getRepresentativeFullName(customer.main_representative) }}
                                </td>

                                <td class="text-center">
                                    <a role="button" class="text-red" @click="onCustomerDetachmentRequest(customer)">
                                        <i class="fa fa-trash"></i>
                                    </a>
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div v-else class="alert alert-danger">Aucun client associé</div>

                    <div class="alert alert-danger" v-if="customerAttachmentError">Ce client est déjà associé à l'utilisateur</div>

                    <div class="row">
                        <div class="col-md-9">
                            <search-and-select ref="customerSearchAndSelect"
                                placeholder="Rechercher un client..."
                                :multiple="false"
                                :initial-value="null"
                                :search-config="buyerCustomerSearchConfig"
                                :get-option-label="getCustomerNameWithRepresentativeAndErpId"
                                @field-updated="onCustomerFieldUpdate">
                            </search-and-select>
                        </div>
                        <div class="col-md-3">
                            <button class="btn btn-primary btn-block" 
                                :class="{'disabled': !customerToAttachId}"
                                @click="onCustomerAttachmentRequest">Ajouter</button>
                        </div>
                    </div>
                </div>
            </div>
        </td>
    </tr>
</template>

<script>
    import UserNameUpdateForm from 'components/Users/UserNameUpdateForm'
    import UserPhoneUpdateForm from 'components/Users/UserPhoneUpdateForm'
    import UserEmailUpdateForm from 'components/Users/UserEmailUpdateForm'
    import UserPasswordUpdateForm from 'components/Users/UserPasswordUpdateForm'
    import SearchAndSelect from 'components/Forms/Elements/SearchAndSelect'
    import presentationHelpersMixin from 'mixins/presentationHelpers'
    import searchMixin from 'mixins/search'

    export default {
        data() {
            return {
                isLoading: false,
                customers: [],
                customerToAttachId: null,
                customerAttachmentError: false,
                customerToDetach: null,
                showEditNameIcon: false,
                showEditPhoneIcon: false,
                showEditEmailIcon: false,
                showEditPasswordIcon: false,
                isUpdatingUserName: false,
                isUpdatingUserPhone: false,
                isUpdatingUserEmail: false,
                isUpdatingUserPassword: false
            }
        },

        props: {
            user: {
                required: true
            }
        },

        components: {
            UserNameUpdateForm, UserPhoneUpdateForm, UserEmailUpdateForm, UserPasswordUpdateForm, SearchAndSelect
        },

        mixins: [presentationHelpersMixin, searchMixin],

        created() {
            this.getCustomers()
        },

        computed: {
            isUpdatingUser() {
                return this.isUpdatingUserPhone || this.isUpdatingUserName || this.isUpdatingUserEmail || this.isUpdatingUserPassword
            },

            userFullName() {
                let fullName = ''

                if (this.user.first_name) {
                    fullName += this.user.first_name
                }

                if (this.user.last_name) {
                    if (fullName) {
                        fullName += ' '
                    }
                    fullName += this.user.last_name
                }

                return fullName ? fullName : '-'
            },

            userPhoneNumber() {
                return this.user.phone ? this.user.phone : '-'
            },

            customerToDetachId() {
                return this.customerToDetach ? this.customerToDetach.id : null
            },

            customerToDetachName() {
                return this.customerToDetach ? this.customerToDetach.name : null
            }
        },

        methods: {
            onNameEditIconClick() {
                this.isUpdatingUserName = true
                this.showEditNameIcon = false
            },

            onUserNameUpdate() {
                this.isUpdatingUserName = false

                this.$emit('name-updated', {userId: this.user.id})
            },
            
            onPhoneEditIconClick() {
                this.isUpdatingUserPhone = true
                this.showEditPhoneIcon = false
            },

            onUserPhoneUpdate() {
                this.isUpdatingUserPhone = false

                this.$emit('phone-updated', {userId: this.user.id})
            },

            onEmailEditIconClick() {
                this.isUpdatingUserEmail = true
                this.showEditEmailIcon = false
            },
            
            onUserEmailUpdate() {
                this.isUpdatingUserEmail = false

                this.$emit('email-updated', {userId: this.user.id})
            },

            onPasswordEditIconClick() {
                this.isUpdatingUserPassword = true
                this.showEditPasswordIcon = false
            },
            
            onUserPasswordUpdate() {
                this.isUpdatingUserPassword = false

                this.$emit('password-updated', {userId: this.user.id})
            },

            detachCustomer(customerId) {
                this.isLoading = true

                this.$root.axios.delete(`users/${this.user.id}/customers/${customerId}`)
                    .then(response => this.getCustomers())
                    .finally(() => this.isLoading = false)
            },

            getCustomers() {
                this.isLoading = true

                this.$root.axios.get(`users/${this.user.id}/customers`)
                    .then(response => {
                        this.customers = response.data.customers

                        this.isLoading = false
                        this.$refs.customerSearchAndSelect.selectedOption = null
                    })
            },

            onCustomerDetachmentRequest(customer) {
                this.customerToDetach = customer
            },

            onCustomerDetachmentCancel() {
                this.customerToDetach = null
            },
            
            onCustomerDetachmentConfirm() {
                this.detachCustomer(this.customerToDetachId)
                
                this.customerToDetach = null
            },

            attachCustomer(customerId) {
                this.isLoading = true

                this.$root.axios.post(`users/${this.user.id}/customers`, { customer_id: customerId })
                    .then(response => this.getCustomers())
                    .catch(error => this.customerAttachmentError = true)
                    .finally(() => this.isLoading = false)
            },

            onCustomerAttachmentRequest() {
                this.attachCustomer(this.customerToAttachId)
            },

            onCustomerFieldUpdate(event) {
                this.customerAttachmentError = false

                if (event) {
                    this.customerToAttachId = event.id
                } else {
                    this.customerToAttachId = null
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .row-details {
        & > td {
            padding: 0 20px !important;
        }
        
        .panel {
            margin-top: 20px;
        }

        .confirmation-overlay {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            height: 100%;
            background-color: #fff;
            z-index: 999;

            .overlay-inner {
                position: absolute;
                left: 50%;
                top: 50%;
                transform: translate(-50%, -50%);
                text-align: center;
            }
        }

        .table tbody tr td {
            padding: 8px;
        }

        .flex-list {
            display: flex;

            .flex-item {
                width: 100%;
            }
        }

        .editable {
            cursor: pointer;

            &:hover, &:focus {
                color: #fb557a;
            }
        }
    }
</style>



// WEBPACK FOOTER //
// client/components/Users/UserRowDetail.vue